import { useEffect, useState } from "react";
import { Eye, EyeOff, KeyRound, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { BankPayouts } from "@/components/BankPayouts";
import { PayoutStatements } from "@/components/PayoutStatements";
import { NotificationPrefs } from "@/components/NotificationPrefs";
import { Associates } from "@/components/Associates";
import { DataRights } from "@/components/DataRights";
import { BillingDetails, TaxInvoices } from "@/components/TaxInvoices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "rounded-none focus:ring-2 focus:ring-blue-800";

export default function Profile() {
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);

  const load = () => api.get("/auth/activity").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) return setError("The two new passwords do not match.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/password/change", {
        current_password: pwd.current, new_password: pwd.next,
      });
      toast.success(data.message);
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <PageHeader
        eyebrow="My account"
        title="Profile & security"
        description="Change your password and review where your account has recently been signed in."
        testId="profile-header"
      />

      <div className="grid gap-10 lg:grid-cols-2">
        {user?.role === "arbitrator" && (
          <div className="lg:col-span-2 space-y-10">
            <BankPayouts />
            <PayoutStatements />
          </div>
        )}
        {user?.role === "advocate" && <div className="lg:col-span-2"><Associates /></div>}
        {user?.role !== "owner" && <div className="lg:col-span-2"><NotificationPrefs /></div>}
        {user?.role !== "owner" && <div className="lg:col-span-2"><DataRights /></div>}
        {["advocate", "client"].includes(user?.role) && <div className="lg:col-span-2"><BillingDetails /></div>}
        {user?.role !== "owner" && <div className="lg:col-span-2"><TaxInvoices /></div>}
        <section className="border border-border bg-card p-7">
          <p className="label-caps">On record</p>
          <p className="font-display text-2xl mt-2" data-testid="profile-name">{user?.name}</p>
          <dl className="mt-5 space-y-2.5 text-sm">
            <div className="flex justify-between gap-6">
              <dt className="text-muted-foreground">Email</dt>
              <dd data-testid="profile-email">{user?.email}</dd>
            </div>
            <div className="flex justify-between gap-6">
              <dt className="text-muted-foreground">Role</dt>
              <dd data-testid="profile-role">{ROLE_LABELS[user?.role] || user?.role}</dd>
            </div>
            {user?.phone && (
              <div className="flex justify-between gap-6">
                <dt className="text-muted-foreground">Mobile</dt>
                <dd data-testid="profile-phone">{user.phone}</dd>
              </div>
            )}
          </dl>

          <form onSubmit={submit} className="mt-9 space-y-5" data-testid="change-password-form">
            <p className="flex items-center gap-2 label-caps">
              <KeyRound className="h-3.5 w-3.5" /> Change password
            </p>
            <div className="space-y-2">
              <Label className="label-caps">Current password</Label>
              <div className="relative">
                <Input data-testid="current-password-input" type={show ? "text" : "password"} required
                  value={pwd.current} onChange={(e) => setPwd((p) => ({ ...p, current: e.target.value }))}
                  className={`${inputCls} pr-10`} />
                <button type="button" data-testid="profile-password-toggle" onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide passwords" : "Show passwords"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-200">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-caps">New password</Label>
              <Input data-testid="new-password-input" type={show ? "text" : "password"} required minLength={6}
                value={pwd.next} onChange={(e) => setPwd((p) => ({ ...p, next: e.target.value }))}
                className={inputCls} />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Confirm new password</Label>
              <Input data-testid="confirm-password-input" type={show ? "text" : "password"} required
                value={pwd.confirm} onChange={(e) => setPwd((p) => ({ ...p, confirm: e.target.value }))}
                className={inputCls} />
            </div>
            {error && <p className="text-sm text-red-800 dark:text-red-400" data-testid="change-password-error">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="change-password-button"
              className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-6">
              {busy ? "Saving…" : "Update password"}
            </Button>
          </form>
        </section>

        <section>
          <p className="flex items-center gap-2 label-caps">
            <MonitorSmartphone className="h-3.5 w-3.5" /> Recent sign-in activity
          </p>
          <p className="text-sm text-muted-foreground mt-3">
            The last fifteen times your account was opened, with the method, device and network address.
          </p>
          {rows.length === 0 ? (
            <div className="mt-6">
              <EmptyState testId="activity-empty" title="No sign-in recorded yet"
                hint="Your next sign-in will be listed here." />
            </div>
          ) : (
            <ul className="mt-6 divide-y divide-border border border-border bg-card" data-testid="activity-list">
              {rows.map((r, i) => (
                <li key={r.id} className="px-5 py-4" data-testid={`activity-row-${i}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                    <p className="text-sm font-medium">{fmtDateTime(r.at)}</p>
                    <p className="label-caps">{r.method}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1.5">
                    {r.device} · <span className="font-mono text-xs">{r.ip}</span>
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
